import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import * as axiosInstance from "../utils/axiosinstace";
import { getUserName, getRole } from "../utils/common";
import { Role } from "../utils/role";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import { useLoading, Bars } from "@agney/react-loading";
import moment from "moment";
import { PieChartOutlined } from '@ant-design/icons';
import HeatStressBubbleChart from "../dashboard/heatStressBubbleChart";
import { useTranslation } from 'react-i18next';

const HeatStressAnalytics = () => {
  const { t } = useTranslation(['analytics', 'common']);
  const { containerProps, indicatorEl } = useLoading({
    loading: true,
    indicator: <Bars width="30" color="#333" />,
  });

  const [role] = useState(getRole());
  const [farmData, setFarmData] = useState([]);
  const [batchData, setBatchData] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [selectedFarmer, setSelectedFarmer] = useState("");
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [preSelectedBatches, setPreSelectedBatches] = useState([]);
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const dropdownRef = useRef(null);
  const lastFarmIdRef = useRef(null);
  const [generateTrigger, setGenerateTrigger] = useState(0);
  const [showChart, setShowChart] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [mode, setMode] = useState("batch");
  const [batchNamesMap, setBatchNamesMap] = useState({});

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({
    defaultValues: {},
  });

  // Watch form fields for validation
  const watchedFields = watch();

  // API function for getting batches by time interval
  const getBatchesByTimeInterval = async (farmID, startDate, endDate) => {
    try {
      const response = await axiosInstance.getBatchesByTimeInterval(
        startDate,
        endDate,
        farmID,
      );
      console.log(response.list);
      return response.list;
    } catch (err) {
      console.log("Error fetching batches by time interval:", err.message);
      return [];
    }
  };

  const getFarms = async (_userName) => {
    try {
      let userName = getUserName();
      if (role === Role.Admin) userName = _userName;
      const response = await axiosInstance.getListofFarms(userName);
      setFarmData(response.list);
    } catch (err) {
      console.log(err.message);
    }
  };

  const getFarmers = async () => {
    try {
      const response = await axiosInstance.getListofFarmers_admin();
      setFarmers(response.list);
    } catch (err) {
      console.log(err.message);
    }
  };

  const onchangeHandler = async (farmID) => {
    if (isLoadingBatches || lastFarmIdRef.current === farmID) {
      return;
    }
    setIsLoadingBatches(true);
    lastFarmIdRef.current = farmID;

    try {
      console.log(`Fetching batches for farm: ${farmID}`);
      const response = await axiosInstance.getListofBatches(farmID, "closed");
      setBatchData(response.list);
      setSelectedBatches([]);
    } catch (err) {
      setBatchData([]);
      setSelectedBatches([]);
      console.log("Error fetching batches:", err);
    } finally {
      setIsLoadingBatches(false);
    }
  };

  const handleBatchSelection = (batchID, batchName) => {
    let newSelectedBatches;
    if (selectedBatches.some((batch) => batch.id === batchID)) {
      // Remove batch
      newSelectedBatches = selectedBatches.filter(
        (batch) => batch.id !== batchID
      );
    } else {
      // Add batch (max 5)
      if (selectedBatches.length >= 5) {
        alert(t('analytics:heat.max5Batches'));
        return;
      }
      newSelectedBatches = [
        ...selectedBatches,
        { id: batchID, name: batchName },
      ];
    }

    setSelectedBatches(newSelectedBatches);
    setValue(
      "batchIDs",
      newSelectedBatches.map((batch) => batch.id),
      { shouldValidate: true }
    );
  };

  const removeBatch = (batchID) => {
    const newSelectedBatches = selectedBatches.filter(
      (batch) => batch.id !== batchID
    );
    setSelectedBatches(newSelectedBatches);
    setValue(
      "batchIDs",
      newSelectedBatches.map((batch) => batch.id),
      { shouldValidate: true }
    );
  };

  // Button validation logic
  const isFormValid = () => {
    const farmSelected = watchedFields.famrmID;
    const filterMode = watchedFields.filterMode;

    if (!farmSelected || !filterMode) return false;

    if (filterMode === "batch") {
      return selectedBatches.length > 0;
    }

    if (filterMode === "time") {
      const timeFilter = watchedFields.timeFilter;
      return timeFilter && timeFilter !== "";
    }

    return false;
  };

  const onSubmit = async (data) => {
    if (
      data.filterMode === "batch" &&
      (!data.batchIDs || data.batchIDs.length === 0)
    ) {
      alert(t('analytics:heat.select1to5'));
      return;
    }

    if (data.filterMode === "time" && !data.timeFilter) {
      alert(t('analytics:heat.selectInterval'));
      return;
    }
    if(data.filterMode === 'time') {
        data.batchIDs = preSelectedBatches.map((item)=>item.batchID)
    }

    // Start loading
    setIsLoadingChart(true);
    setShowChart(false);
    setChartData([]);

    console.log("Form Data:", {
      ...data,
      selectedBatchCount: data.batchIDs ? data.batchIDs.length : 0,
      preSelectedBatchCount: preSelectedBatches.length,
      preSelectedBatches: preSelectedBatches,
      selectedFarmer: selectedFarmer,
      timestamp: new Date().toISOString(),
    });

    try {
      // Ensure batchIDs is properly formatted as an array
      let batchIDsArray = [];

      if (Array.isArray(data.batchIDs)) {
        batchIDsArray = data.batchIDs;
      } else if (typeof data.batchIDs === 'string') {
        batchIDsArray = data.batchIDs.split(',').map(id => parseInt(id.trim()));
      }

      // Build batch name mapping
      const namesMap = {};
      if (data.filterMode === 'batch') {
        selectedBatches.forEach(batch => {
          namesMap[batch.id] = batch.name;
        });
      } else {
        preSelectedBatches.forEach(batch => {
          namesMap[batch.batchID] = batch.batchName;
        });
      }
      setBatchNamesMap(namesMap);

      // Call Heat Stress Analytics API with batchIDs array
      const payload = {
        batchIDs: batchIDsArray
      };

      console.log("Calling getHeatIndexDetails API with payload:", payload);

      const response = await axiosInstance.getHeatIndexDetails(payload);

      console.log("Heat Stress Analytics API response:", response);

      if (response && response.status && response.data && response.data[0]) {
        setChartData(response.data[0]);
        setShowChart(true);
      } else {
        setChartData([]);
        setShowChart(true);
      }
      setIsLoadingChart(false);

    } catch (err) {
      console.error("Error fetching heat stress analytics:", err);
      alert(t('analytics:heat.loadFailed'));
      setChartData([]);
      setShowChart(false);
      setIsLoadingChart(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowBatchDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (role === Role.Admin) getFarmers();
    else getFarms();
  }, []);

  useEffect(() => {
    const farmerSelect = $("#selectFarmer").select2({
      placeholder: t('common:select.farmer'),
      allowClear: false,
      width: "100%",
    });

    farmerSelect.on("change", function (e) {
      const selectedFarmerID = e.target.value;
      setSelectedFarmer(selectedFarmerID);

      // Reset everything when farmer changes
      setBatchData([]);
      setSelectedBatches([]);
      setPreSelectedBatches([]);
      setShowChart(false);
      setGenerateTrigger(0);

      // Reset form fields
      setValue("famrmID", "", { shouldValidate: false });
      setValue("filterMode", "", { shouldValidate: false });
      setValue("timeFilter", "", { shouldValidate: false });
      setValue("batchIDs", [], { shouldValidate: false });
      setValue("fromDate", "", { shouldValidate: false });
      setValue("toDate", "", { shouldValidate: false });

      // Reset Select2 dropdowns
      $("#selectFarm").val("").trigger("change");
      $("#filterMode").val("").trigger("change");

      // Reset the farm reference
      lastFarmIdRef.current = null;

      if (role === Role.Admin) {
        getFarms(selectedFarmerID);
      }
    });

    const farmSelect = $("#selectFarm").select2({
      placeholder: t('common:select.barn'),
      allowClear: false,
      width: "100%",
    });

    farmSelect.on("change", function (e) {
      const selectedFarmID = e.target.value;

      // Reset everything when farm changes
      setBatchData([]);
      setSelectedBatches([]);
      setPreSelectedBatches([]);
      setShowChart(false);
      setGenerateTrigger(0);

      // Reset form fields
      setValue("filterMode", "", { shouldValidate: false });
      setValue("timeFilter", "", { shouldValidate: false });
      setValue("batchIDs", [], { shouldValidate: false });
      setValue("fromDate", "", { shouldValidate: false });
      setValue("toDate", "", { shouldValidate: false });

      // Reset Select2 dropdowns
      $("#filterMode").val("").trigger("change");
      $("#timeFilter").val("").trigger("change");

      // Set farm ID and fetch batches only if farm is selected
      if (selectedFarmID) {
        trigger("famrmID");
        setValue("famrmID", selectedFarmID, { shouldValidate: true });
        onchangeHandler(selectedFarmID);
      } else {
        setValue("famrmID", "", { shouldValidate: false });
        lastFarmIdRef.current = null;
      }
    });

    const filterModeSelect = $("#filterMode").select2({
      placeholder: t('analytics:heat.selectFilterMode'),
      allowClear: false,
      width: "100%",
    });

    filterModeSelect.on("change", function (e) {
      setValue("filterMode", e.target.value, { shouldValidate: true });
      setMode(e.target.value);
      // Reset batch and time related data when filter mode changes
      setSelectedBatches([]);
      setPreSelectedBatches([]);
      setValue("batchIDs", [], { shouldValidate: false });
      setValue("timeFilter", "", { shouldValidate: false });
      setValue("fromDate", "", { shouldValidate: false });
      setValue("toDate", "", { shouldValidate: false });

      // Reset time filter dropdown
      $("#timeFilter").val("").trigger("change");
    });

    return () => {
      if ($.fn.select2) {
        farmerSelect.select2("destroy");
        farmSelect.select2("destroy");
        filterModeSelect.select2("destroy");
      }
    };
  }, [farmData, farmers, role, selectedFarmer, isLoadingBatches]);

  // Render compact batch display
  const renderBatchDisplay = () => {
    if (selectedBatches.length === 0) {
      return <span className="batch-placeholder">{t('analytics:heat.batchesPlaceholder')}</span>;
    }

    if (selectedBatches.length <= 2) {
      return selectedBatches.map((batch) => (
        <span key={batch.id} className="batch-tag">
          {batch.name}
          <button
            type="button"
            className="remove-btn"
            onClick={(e) => {
              e.stopPropagation();
              removeBatch(batch.id);
            }}
          >
            ×
          </button>
        </span>
      ));
    }

    return (
      <span className="batch-count">
        {selectedBatches.length} batches selected
      </span>
    );
  };

  const computeRange = (timeFilter) => {
    const end = moment().endOf("day");
    const thisMonthStart = end.clone().startOf("month");

    let start;
    if (timeFilter === "3months") {
      start = thisMonthStart.clone().subtract(2, "months");
    } else if (timeFilter === "6months") {
      start = thisMonthStart.clone().subtract(5, "months");
    } else if (timeFilter === "12months") {
      start = thisMonthStart.clone().subtract(11, "months");
    } else {
      return { startDate: "", endDate: "" };
    }

    return {
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
    };
  };

  const onTimeFilterChange = async (e) => {
    const value = e.target.value;

    setValue("timeFilter", value, { shouldValidate: true });

    // Compute dates
    const { startDate, endDate } = computeRange(value);
    setValue("fromDate", startDate, { shouldValidate: true });
    setValue("toDate", endDate, { shouldValidate: true });

    // Current farm
    const currentFarmID = watch("famrmID");

    if (value && currentFarmID) {
      try {
        const batches = await getBatchesByTimeInterval(
          currentFarmID,
          startDate,
          endDate
        );

        setPreSelectedBatches(batches);
      } catch (error) {
        console.error("Error loading batches for time interval:", error);
        setPreSelectedBatches([]);
      }
    } else {
      setPreSelectedBatches([]);
    }
  };

  return (
    <>
      <div className="batchOperations">
        <div className="row mb-3">
          <div className="col-sm-12">
            <h4 className="m-0 text-dark">{t('analytics:heat.title')}</h4>
          </div>
        </div>

        <div className="batch-comparison-container">
          <div className="row">
            {/* Filters Sidebar - 1/4 of page (col-md-3) */}
            <div className="col-md-3">
              <div className="filters-sidebar">
                <div className="sidebar-title">{t('common:actions.applyFilters')}</div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  {/* Filter Section 1: Farmer Selection */}
                  {role === Role.Admin && (
                    <div className="filter-section">
                      <h6>{t('common:select.farmer')}</h6>
                      <div className="form-group">
                        <select
                          id="selectFarmer"
                          className="form-control form-control-sm"
                        >
                          <option value="">{t('common:select.farmerDashed')}</option>
                          {farmers.map((item) => (
                            <option key={item.id} value={item.userName}>
                              {item.farmerName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Filter Section 2: Barn Selection */}
                  <div className="filter-section">
                    <h6>{t('common:select.barn')}</h6>
                    <div className="form-group">
                      <select
                        className="form-control form-control-sm"
                        name="famrmID"
                        id="selectFarm"
                        {...register("famrmID", {
                          required: t('forms:select.barn'),
                        })}
                      >
                        <option value="">{t('common:select.barnDashed')}</option>
                        {farmData.map((item) => (
                          <option key={item.farmID} value={item.farmID}>
                            {item.farmName}
                          </option>
                        ))}
                      </select>
                      {errors.famrmID && (
                        <div className="text-danger">
                          {errors.famrmID.message}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Filter Section 3: Filter Mode */}
                  <div className="filter-section">
                    <h6>{t('common:fields.filterMode')}</h6>
                    <div className="form-group">
                      <select
                        className="form-control form-control-sm"
                        id="filterMode"
                        {...register("filterMode", {
                          required: t('forms:select.filterMode'),
                        })}
                      >
                        <option value="">-Select Mode-</option>
                        <option value="batch">{t('common:select.batches')}</option>
                        <option value="time">{t('common:fields.timeInterval')}</option>
                      </select>
                      {errors.filterMode && (
                        <div className="text-danger">
                          {errors.filterMode.message}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Filter Section 4: Batch Selection */}
                  {watchedFields.filterMode === "batch" && (
                    <div className="filter-section">
                      <h6>{t('common:select.batches')}</h6>
                      <div className="form-group">
                        <label>{t('analytics:heat.chooseUpTo5')}</label>
                        <div className="batch-multi-select" ref={dropdownRef}>
                          <div
                            className={`batch-display ${
                              showBatchDropdown ? "open" : ""
                            }`}
                            onClick={() =>
                              setShowBatchDropdown(!showBatchDropdown)
                            }
                          >
                            {renderBatchDisplay()}
                          </div>

                          {showBatchDropdown && (
                            <div className="batch-dropdown">
                              <div className="dropdown-header">
                                Select batches ({selectedBatches.length}/5)
                              </div>
                              {batchData.length === 0 ? (
                                <div className="no-batches">
                                  {t('analytics:heat.noBatchesSelectBarn')}
                                  first.
                                </div>
                              ) : (
                                batchData.map((item) => (
                                  <div
                                    key={item.batchID}
                                    className="batch-option"
                                    onClick={() =>
                                      handleBatchSelection(
                                        item.batchID,
                                        item.batchName
                                      )
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedBatches.some(
                                        (batch) => batch.id === item.batchID
                                      )}
                                      onChange={() => {}}
                                    />
                                    <label>{item.batchName}</label>
                                  </div>
                                ))
                              )}
                            </div>
                          )}

                          <input
                            type="hidden"
                            {...register("batchIDs")}
                            value={selectedBatches
                              .map((batch) => batch.id)
                              .join(",")}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Filter Section 4: Time Interval */}
                  {watchedFields.filterMode === "time" && (
                    <div className="filter-section">
                      <h6>{t('common:fields.timeInterval')}</h6>
                      <div className="form-group">
                        <label>{t('common:select.timeRange')}</label>
                        <select
                          className="form-control"
                          id="timeFilter"
                          {...register("timeFilter", {
                            required: t('forms:select.interval'),
                          })}
                          onChange={onTimeFilterChange}
                          value={watch("timeFilter") || ""}
                        >
                          <option value="">-Select Interval-</option>
                          <option value="3months">{t('analytics:heat.last3Months')}</option>
                          <option value="6months">{t('analytics:heat.last6Months')}</option>
                          <option value="12months">{t('analytics:heat.last1Year')}</option>
                        </select>
                        {errors.timeFilter && (
                          <div className="validation-error">
                            {errors.timeFilter.message}
                          </div>
                        )}
                      </div>

                      {preSelectedBatches.length > 0 && (
                        <div className="pre-selected-batches">
                          <h6>{t('common:fields.foundBatches')}{preSelectedBatches.length})</h6>
                          <div className="pre-selected-batch-list">
                            {preSelectedBatches.map((batch) => (
                              <span
                                key={batch.batchID}
                                className="pre-selected-batch-item"
                              >
                                {batch.batchName}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {watch("timeFilter") &&
                        preSelectedBatches.length === 0 &&
                        watch("famrmID") && (
                          <div
                            className="pre-selected-batches"
                            style={{ textAlign: "center" }}
                          >
                            <div style={{ color: "#6c757d", fontSize: "12px" }}>
                              {t('analytics:compare.noBatchesInterval')}
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  {/* Generate Report Button */}
                  {watchedFields.filterMode && (
                    <div className="generate-btn-section">
                      <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={!isFormValid()}
                      >
                        {t('common:actions.generateReport')}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Results Area - 3/4 of page (col-md-9) */}
            <div className="col-md-9">
              <div className="card">
                <div className="card-body">
                  <div className="">
                    {isLoadingChart ? (
                      <div className="placeholder-message">
                        <div className="placeholder-icon">
                          <div {...containerProps}>
                            {indicatorEl}
                          </div>
                        </div>
                        <div className="placeholder-title">
                          {t('analytics:heat.loading')}
                        </div>
                      </div>
                    ) : showChart ? (
                      <HeatStressBubbleChart
                        data={chartData}
                        batchNames={batchNamesMap}
                        title={t('analytics:heat.chartTitle')}
                      />
                    ) : (
                      <div className="placeholder-message">
                        <div className="placeholder-icon">
                          <PieChartOutlined style={{ fontSize: 40, color: '#4e4ead' }}/>
                        </div>
                        <div className="placeholder-title">
                          {t('analytics:compare.readyForAnalysis')}
                        </div>
                        <div className="placeholder-text">
                          {t('analytics:heat.configureFilters')}
                        </div>
                        <div className="placeholder-steps">
                          <h6>{t('analytics:heat.quickStart')}</h6>
                          <ol>
                            {role === Role.Admin ? (
                              <>
                                <li>{t('analytics:heat.stepFarmer')}</li>
                                <li>{t('analytics:heat.stepBarn')}</li>
                                <li>{t('analytics:heat.stepFilterMode')}</li>
                                <li>{t('analytics:heat.stepCriteria')}</li>
                                <li>{t('analytics:compare.stepGenerate')}</li>
                              </>
                            ) : (
                              <>
                                <li>{t('analytics:heat.stepBarnAlt')}</li>
                                <li>{t('analytics:heat.stepFilterMode')}</li>
                                <li>{t('analytics:heat.stepCriteria')}</li>
                                <li>{t('analytics:compare.stepGenerate')}</li>
                              </>
                            )}
                          </ol>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HeatStressAnalytics;
